/*
 * The chat tab.
 *
 * Two things about it are dictated by the API rather than chosen.
 *
 * First, every turn is an OPERATION. There is no way to bind an ordinary run to a
 * knowledge base — `POST /v1/runs` takes no base — so `POST .../operations` is the
 * only route that mounts the planes, and the composer's `/ingest` `/query` `/lint`
 * chips are therefore how the request is named, not garnish on a text box.
 *
 * Second, the conversation follows the base, not the browser. Every session an
 * operation creates carries a `knowledge_base_id`, and `GET /v1/sessions` filters
 * by it — so the transcript shown here is: the session this browser last used
 * (kept in local storage as a preference), or failing that the newest session the
 * API records against this base, which is what makes the conversation reappear on
 * another device.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { listMessages, type Message } from '../../../api/client.ts'
import {
  latestSessionFor,
  type KnowledgeBase,
  type OperationRequest,
  type SourceFile,
  type WikiContent,
} from '../../../api/knowledgeBases.ts'
import type { Lease } from '../lib/operations.ts'
import type { OperationRun, Step, ToolStep } from '../lib/useOperationRun.ts'
import { ago, bytes, num, shortStamp } from '../lib/format.ts'
import { IngestCard } from '../components/IngestCard.tsx'
import { Empty } from '../components/AsyncState.tsx'
import type { Resource } from '../lib/useResource.ts'

type OpKind = 'query' | 'ingest' | 'lint'

const CHIPS: { kind: OpKind; label: string; hint: string }[] = [
  { kind: 'query', label: '/query', hint: 'спросить по базе; ответ можно подшить страницей' },
  { kind: 'ingest', label: '/ingest', hint: 'проиндексировать выбранные источники' },
  { kind: 'lint', label: '/lint', hint: 'проверить wiki на сироты, устаревшее и пропуски' },
]

const SESSION_KEY_PREFIX = 'protocore-kb:session:'

interface ChatTabProps {
  base: KnowledgeBase
  run: OperationRun
  lease: Lease
  sources: Resource<{ files?: SourceFile[]; total: number }>
  wikiIndex: Resource<{ page_count: number; total_bytes: number }>
  indexPage: Resource<WikiContent>
  onOpenTab: (tab: 'history' | 'wiki' | 'files' | 'limits') => void
}

export function ChatTab({
  base,
  run,
  lease,
  sources,
  wikiIndex,
  indexPage,
  onOpenTab,
}: ChatTabProps) {
  const [kind, setKind] = useState<OpKind>('query')
  const [text, setText] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [messages, setMessages] = useState<Message[] | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(() => rememberedSession(base.id))
  const streamEnd = useRef<HTMLDivElement>(null)

  // The active run's session wins: it is the conversation now on screen.
  useEffect(() => {
    if (!run.active) return
    setSessionId(run.active.sessionId)
    rememberSession(base.id, run.active.sessionId)
  }, [base.id, run.active])

  useEffect(() => {
    setSessionId(rememberedSession(base.id))
    setMessages(null)
  }, [base.id])

  // A browser with no local pointer asks the API which conversation is this
  // base's newest — that is what carries a transcript to a second device.
  useEffect(() => {
    if (sessionId !== null) return
    let cancelled = false
    void latestSessionFor(base.id)
      .then((found) => {
        if (cancelled || found === null) return
        setSessionId(found)
        rememberSession(base.id, found)
      })
      .catch(() => {
        // No answer is an empty chat, which the composer already explains.
      })
    return () => {
      cancelled = true
    }
  }, [base.id, sessionId])

  const loadTranscript = useCallback(async () => {
    if (!sessionId) {
      setMessages([])
      return
    }
    try {
      const response = await listMessages(sessionId)
      setMessages(response.items)
    } catch {
      // A session this browser remembers may have been archived or belong to
      // another scope now. An empty transcript is the truthful rendering of that,
      // and the live stream is unaffected.
      setMessages([])
    }
  }, [sessionId])

  useEffect(() => {
    void loadTranscript()
  }, [loadTranscript])

  useEffect(() => {
    if (run.phase === 'settled') void loadTranscript()
  }, [run.phase, loadTranscript])

  useEffect(() => {
    streamEnd.current?.scrollIntoView({ block: 'end' })
  }, [run.steps.length, run.phase, messages?.length])

  const files = sources.data?.files ?? []
  const busy = run.phase === 'starting' || run.phase === 'streaming' || run.phase === 'reconciling'
  const canSend = useMemo(() => {
    if (busy) return false
    if (kind === 'query') return text.trim().length > 0
    if (kind === 'ingest') return picked.size > 0
    return true
  }, [busy, kind, text, picked])

  async function send() {
    if (!canSend) return
    const request = buildRequest(kind, text, [...picked])
    await run.start(request, promptFor(kind, text, [...picked]))
    setText('')
    setPicked(new Set())
  }

  return (
    <div className="chat">
      <div className="chat__main">
        <div className="stream">
          {/* Durable turns, then whatever is live on top of them. */}
          {messages === null ? (
            <p className="muted-2" style={{ fontSize: 12.5 }}>
              Загружаем переписку…
            </p>
          ) : messages.length === 0 && run.steps.length === 0 && !run.active ? (
            <Empty title="Разговора ещё не было">
              Выберите операцию в композере ниже. <code>/ingest</code> проиндексирует выбранные
              источники, <code>/query</code> ответит по уже написанной wiki, <code>/lint</code>{' '}
              проверит её на сироты и устаревшие утверждения.
            </Empty>
          ) : (
            messages.map((message) => <DurableMessage key={message.id} message={message} />)
          )}

          {run.active ? (
            <div className="msg">
              <div className="msg__av msg__av--user">Вы</div>
              <div className="msg__body">
                <div className="msg__who">Вы · {shortStamp(new Date().toISOString())}</div>
                <div className="bubble">{run.active.prompt}</div>
              </div>
            </div>
          ) : null}

          {run.steps.length > 0 || busy || run.report ? (
            <div className="msg">
              <div className="msg__av msg__av--agent" aria-hidden="true">
                ◈
              </div>
              <div className="msg__body">
                <div className="msg__who">
                  Protocore
                  {run.active ? ` · ${run.active.request.op}` : ''}
                  {run.runStatus ? ` · ${run.runStatus}` : busy ? ' · идёт' : ''}
                </div>
                {run.active && !run.active.wikiWritable ? (
                  <div className="callout" style={{ marginBottom: 8 }}>
                    <b>Wiki для этой операции — только чтение.</b> Право записи держал другой
                    ран, когда она стартовала; изменений страниц не будет.
                  </div>
                ) : null}
                <StepList steps={run.steps} />
                {busy && run.steps.length === 0 ? (
                  <p className="muted-2" style={{ fontSize: 12.5 }}>
                    Операция запущена, ждём первые события…
                  </p>
                ) : null}
                {run.report ? (
                  <IngestCard
                    op={run.report.op}
                    diff={run.report.diff}
                    problem={run.report.problem}
                    undoLeftMs={run.undoLeftMs}
                    reverting={run.reverting}
                    onRevert={() => void run.revert()}
                    onDismissUndo={run.dismissUndo}
                    onOpenInHistory={() => onOpenTab('history')}
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          {run.error ? (
            <div className="callout callout--danger">
              <b>Операция не выполнена.</b> {run.error}
            </div>
          ) : null}

          <div ref={streamEnd} />
        </div>

        <div className="composer">
          <div className="composer__box">
            <div className="composer__chips">
              {CHIPS.map((chip) => (
                <button
                  key={chip.kind}
                  type="button"
                  className="chip"
                  aria-pressed={kind === chip.kind}
                  title={chip.hint}
                  onClick={() => setKind(chip.kind)}
                >
                  {chip.label}
                </button>
              ))}
              <span className="muted-2" style={{ fontSize: 11, alignSelf: 'center' }}>
                {CHIPS.find((chip) => chip.kind === kind)?.hint}
              </span>
            </div>

            {kind === 'ingest' ? (
              <SourcePicker
                files={files}
                picked={picked}
                loading={sources.status === 'loading'}
                onToggle={(path) =>
                  setPicked((current) => {
                    const next = new Set(current)
                    if (next.has(path)) next.delete(path)
                    else next.add(path)
                    return next
                  })
                }
                onOpenFiles={() => onOpenTab('files')}
              />
            ) : null}

            <div className="composer__row">
              <textarea
                value={text}
                rows={1}
                placeholder={placeholderFor(kind)}
                disabled={kind === 'lint'}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    void send()
                  }
                }}
              />
              <button
                type="button"
                className="composer__send"
                disabled={!canSend}
                aria-label="Запустить операцию"
                onClick={() => void send()}
              >
                ↑
              </button>
            </div>

            <div className="composer__foot">
              <span>◉ база: {base.name}</span>
              <span>
                {lease.held
                  ? lease.mine
                    ? '◐ право записи: у этой операции'
                    : '◐ право записи: у другой операции'
                  : '○ право записи свободно'}
              </span>
              <button
                type="button"
                className="link-btn"
                style={{ fontSize: 10.5 }}
                onClick={() => onOpenTab('limits')}
              >
                лимиты и их источники
              </button>
            </div>
          </div>
        </div>
      </div>

      <aside className="ctx">
        <section>
          <h4>Состояние wiki</h4>
          <div className="kv">
            <span>Страниц</span>
            <b>{wikiIndex.data ? num(wikiIndex.data.page_count) : num(base.wiki_pages ?? 0)}</b>
          </div>
          <div className="kv">
            <span>Источников</span>
            <b>{num(sources.data?.total ?? base.raw_files ?? 0)}</b>
          </div>
          <div className="kv">
            <span>Вес wiki</span>
            <b>{bytes(wikiIndex.data?.total_bytes ?? base.wiki_bytes ?? 0)}</b>
          </div>
          <div className="kv">
            <span>Последний ingest</span>
            <b>{base.last_ingest_at ? ago(base.last_ingest_at) : 'не запускался'}</b>
          </div>
          <div className="kv">
            <span>Последний lint</span>
            <b>{base.last_lint_at ? ago(base.last_lint_at) : 'не запускался'}</b>
          </div>
          <div className="kv">
            <span>Ревизия KB.md</span>
            <b>r{base.schema_revision ?? 1}</b>
          </div>
          <div className="kv">
            <span>HEAD</span>
            <b className="mono">{base.git_head ? base.git_head.slice(0, 7) : '—'}</b>
          </div>
        </section>

        <section>
          <h4>Право записи</h4>
          {lease.held ? (
            <div className="lease-box">
              {lease.mine
                ? 'Удерживает текущая операция.'
                : lease.op
                  ? `Удерживает операция «${lease.op.title || lease.op.op}».`
                  : 'Удерживает другая операция.'}
              {lease.expiresAt ? ` Удерживается до ${shortStamp(lease.expiresAt)}.` : ''} Второй
              ран на эту базу получит <b>wiki только для чтения</b> — query и чтение продолжат
              работать.
            </div>
          ) : (
            <p className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.6, margin: 0 }}>
              Свободно: следующая операция сможет писать в <code>wiki/</code>.
            </p>
          )}
        </section>

        <section>
          <h4>
            index.md — выдержка{' '}
            <button
              type="button"
              className="link-btn"
              style={{ fontSize: 10 }}
              onClick={() => onOpenTab('wiki')}
            >
              открыть
            </button>
          </h4>
          {indexPage.status === 'ready' && indexPage.data ? (
            <div className="index-excerpt">{excerpt(indexPage.data.content)}</div>
          ) : indexPage.status === 'loading' ? (
            <span className="skeleton" style={{ height: 64, borderRadius: 9 }} />
          ) : (
            <p className="muted-2" style={{ fontSize: 11.5, margin: 0, lineHeight: 1.6 }}>
              Каталог недоступен{indexPage.httpStatus ? ` · ${indexPage.httpStatus}` : ''}. Агент
              поддерживает <code>index.md</code> на каждом ingest и читает его первым при query.
            </p>
          )}
        </section>
      </aside>
    </div>
  )
}

/**
 * Render the live transcript, grouping consecutive tool calls into one block.
 *
 * The grouping is what reproduces the real shape of a run — prose, a cluster of
 * tool calls, more prose — instead of a flat list of alternating rows.
 */
function StepList({ steps }: { steps: Step[] }) {
  const groups: Step[][] = []
  for (const step of steps) {
    const last = groups[groups.length - 1]
    const isTool = step.kind === 'tool'
    if (last && last[0] !== undefined && (last[0].kind === 'tool') === isTool) last.push(step)
    else groups.push([step])
  }

  return (
    <>
      {groups.map((group, index) =>
        group[0]?.kind === 'tool' ? (
          <div className="tools" key={index}>
            {(group as ToolStep[]).map((step) => (
              <div className="tool" key={step.id}>
                <span className="tool__name">{step.name}</span>
                <span className="tool__arg">{step.arg}</span>
                <span
                  className={`tool__tick${step.state === 'running' ? ' tool__tick--pending' : ''}`}
                >
                  {step.state === 'running' ? '…' : step.state === 'ok' ? '✓' : '✕'}
                  {step.detail ? ` ${step.detail}` : ''}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div
            key={index}
            className={group[0]?.kind === 'thinking' ? 'prose prose--thinking' : 'prose'}
          >
            {group.map((step) => (step as { text: string }).text).join('')}
          </div>
        ),
      )}
    </>
  )
}

function DurableMessage({ message }: { message: Message }) {
  const text = (message.parts ?? [])
    .filter((part) => part.type === 'text' && part.visibility === 'public')
    .map((part) => part.text ?? part.preview_text ?? '')
    .join('')
    .trim()
  if (!text) return null
  const mine = message.role === 'user'
  return (
    <div className="msg">
      <div className={`msg__av ${mine ? 'msg__av--user' : 'msg__av--agent'}`} aria-hidden="true">
        {mine ? 'Вы' : '◈'}
      </div>
      <div className="msg__body">
        <div className="msg__who">
          {mine ? 'Вы' : 'Protocore'} · {shortStamp(message.created_at)}
        </div>
        {mine ? <div className="bubble">{text}</div> : <div className="prose">{text}</div>}
      </div>
    </div>
  )
}

function SourcePicker({
  files,
  picked,
  loading,
  onToggle,
  onOpenFiles,
}: {
  files: SourceFile[]
  picked: Set<string>
  loading: boolean
  onToggle: (path: string) => void
  onOpenFiles: () => void
}) {
  if (loading) {
    return (
      <div className="composer__sources" style={{ padding: 11 }}>
        <span className="skeleton" style={{ width: '70%' }} />
      </div>
    )
  }
  if (files.length === 0) {
    return (
      <div className="composer__sources" style={{ padding: '11px 12px' }}>
        <span className="muted-2" style={{ fontSize: 11.5 }}>
          В <code>raw/</code> пока нет файлов.{' '}
          <button type="button" className="link-btn" onClick={onOpenFiles}>
            Загрузить источники
          </button>
        </span>
      </div>
    )
  }
  return (
    <div className="composer__sources">
      {files.map((file) => (
        <button
          key={file.path}
          type="button"
          className="composer__source"
          aria-pressed={picked.has(file.path)}
          onClick={() => onToggle(file.path)}
        >
          <span className="box" aria-hidden="true">
            {picked.has(file.path) ? '✓' : ''}
          </span>
          <span className="name">{file.path}</span>
          <span className="size">{bytes(file.size_bytes)}</span>
        </button>
      ))}
    </div>
  )
}

function buildRequest(kind: OpKind, text: string, sources: string[]): OperationRequest {
  if (kind === 'ingest') return { op: 'ingest', sources }
  if (kind === 'lint') return { op: 'lint' }
  return { op: 'query', question: text.trim() }
}

function promptFor(kind: OpKind, text: string, sources: string[]): string {
  if (kind === 'ingest') {
    return `/ingest ${sources.join(', ')}${text.trim() ? `\n${text.trim()}` : ''}`
  }
  if (kind === 'lint') return '/lint'
  return text.trim()
}

function placeholderFor(kind: OpKind): string {
  if (kind === 'ingest') return 'Что учесть при индексации (необязательно)…'
  if (kind === 'lint') return 'Lint запускается без текста'
  return 'Спросить по базе…'
}

/** First lines of `index.md`, enough to see the catalog's shape in the panel. */
function excerpt(content: string, lines = 16): string {
  return content.split('\n').slice(0, lines).join('\n')
}

function sessionKey(kbId: string): string {
  return `${SESSION_KEY_PREFIX}${kbId}`
}

function rememberedSession(kbId: string): string | null {
  try {
    return window.localStorage.getItem(sessionKey(kbId))
  } catch {
    // Private-mode storage refusals are not worth a broken tab.
    return null
  }
}

function rememberSession(kbId: string, sessionId: string): void {
  try {
    window.localStorage.setItem(sessionKey(kbId), sessionId)
  } catch {
    // See above: losing the transcript across reloads beats failing to render.
  }
}
